import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  type PairingPaymentsFilter,
  usePairingPaymentsQuery,
} from "@/modules/pairingPayments/api/usePairingPayments.ts";
import {
  getFilterValue,
  getItemPerPage,
  getSortingValue,
  getVisibilityStateValue,
  setFilterValue,
  setItemPerPage,
  setVisibilityStateValue,
  sortingParser,
} from "@/lib/table.ts";
import { keepPreviousData } from "@tanstack/react-query";
import { pairingColumns } from "@/modules/pairingPayments/TableColumnsDef.tsx";
import { columnLabelsMap } from "@/modules/pairingPayments/TableColumnsDef.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { Loader } from "@/components/Loader.tsx";
import { DataTable } from "@/components/table/DataTable.tsx";
import { parseAsIndex, useQueryState } from "nuqs";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { FilterProvider, type FilterProviderOnChange } from "@/components/table/FilterProvider.tsx";
import { Export } from "@/modules/pairingPayments/Export.tsx";

const PAIRING_FILTERS_KEY = "@fin_merch_pairing_filters";
const PAIRING_COLUMN_VISIBILITY_KEY = "@fin_merch_pairing_visible_columns";
export const PAIRING_LOCATION_KEY = "@fin_merch_pairing_location";
export const Route = createFileRoute("/payments/pairing/")({
  component: RouteComponent,
});

function RouteComponent() {
  const title = t`Pairing payments`;
  const { activeApp } = useAppConfig();

  const [sorting, setSorting] = useQueryState<SortingState>(
    "sort",
    sortingParser.withDefault([{ id: "createdAt", desc: true }]),
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getVisibilityStateValue(PAIRING_COLUMN_VISIBILITY_KEY),
  );

  useEffect(() => {
    setVisibilityStateValue(PAIRING_COLUMN_VISIBILITY_KEY, columnVisibility);
  }, [columnVisibility]);

  const [filter, setFilter] = useState<PairingPaymentsFilter>(getFilterValue(PAIRING_FILTERS_KEY));
  const onFilterChange: FilterProviderOnChange = (values) => {
    setFilter((prevState) => ({ ...prevState, ...values }));
    setFilterValue(PAIRING_FILTERS_KEY, { ...filter, ...values });
  };

  const [page, setPage] = useQueryState<number>("page", parseAsIndex.withDefault(0));
  const [perPage, setPerPage] = useState<number>(getItemPerPage());

  const pagination: PaginationState = { pageIndex: page, pageSize: perPage };
  const setPagination: OnChangeFn<PaginationState> = (value) => {
    if ("pageIndex" in value) return;

    const { pageIndex, pageSize } = value(pagination);
    void setPage(pageIndex);
    void setPerPage(pageSize);
    setItemPerPage(pageSize);
  };

  const { data, isPending, isError, error } = usePairingPaymentsQuery(
    {
      pageNumber: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: getSortingValue(sorting ?? []),
      ...filter,
    },
    { merchantId: activeApp },
    { enabled: !!activeApp, placeholderData: keepPreviousData },
  );

  const columnsConfig = useMemo(pairingColumns, []);

  const table = useReactTable({
    data: data?.items ? data.items : [],
    columns: columnsConfig,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.pagination?.totalItemsCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    state: { sorting, columnVisibility, pagination },
  });

  const labels = useMemo(columnLabelsMap, []);

  return (
    <FilterProvider value={filter} onChange={onFilterChange}>
      <PageWrapper>
        <PageHeader breadcrumbs={[{ href: "/", title }]} />
        <div className="flex flex-col grow p-5">
          {isError && (
            <FetchingError
              message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
              error={error}
            />
          )}
          {isPending && <Loader message={t`Loading pairing payments...`} />}
          {!isPending && !isError && (
            <DataTable
              table={table}
              labels={labels}
              detailRoute={"/payments/pairing/$id"}
              detailIdKey={"id"}
              exportComponent={<Export table={table} filter={filter} />}
              locationKey={PAIRING_LOCATION_KEY}
            />
          )}
        </div>
      </PageWrapper>
    </FilterProvider>
  );
}
